import * as Yup from 'yup';

import { IndexType } from '../../../Types/enums';

const regexNoStartEndWhiteSpace = /^\S*(\s+[^\s]+)*$/;
const errorMessageNoWhiteSpace = 'Cannot start or end with white space.';
const errorMessageRequired = 'This field is required.';
const errorMessageInvalidPath = 'Invalid path format.';
const noWhitespaceStartEnd = Yup.string().matches(/^\S.*\S$|^\S$/, errorMessageNoWhiteSpace);

const excludedFolderSchema = Yup.array()
  .of(
    Yup.string()
      .required(errorMessageRequired)
      .test('no-empty-path', errorMessageInvalidPath, (val) => {
        if (!val) return false;
        const trimmed = val.trim();
        if (trimmed === '') return false;
        if (/\/{2,}/.test(trimmed)) return false; // no double slashes
        return true;
      })
      .concat(noWhitespaceStartEnd)
  )
  .test('no-duplicates', 'Folder paths must be unique.', (list) => {
    if (!list) return true;
    const set = new Set(list.map((item) => item?.trim()));
    return set.size === list.length;
  })
  .optional();

const excludedFileSchema = Yup.array()
  .of(
    Yup.string()
      .required(errorMessageRequired)
      .test('valid-file-path', errorMessageInvalidPath, (val) => {
        if (!val) return false;
        const trimmed = val.trim();
        if (trimmed === '') return false;
        if (/\/{2,}/.test(trimmed)) return false;
        if (!/[^/]+\.[\w\d]+$/.test(trimmed)) return false; // must end in a file.ext
        return true;
      })
      .concat(noWhitespaceStartEnd)
  )
  .test('no-duplicates', 'File paths must be unique.', (list) => {
    if (!list) return true;
    const set = new Set(list.map((item) => item?.trim()));
    return set.size === list.length;
  })
  .optional();

export const baseSchema = Yup.object({
  name: Yup.string()
    .required(errorMessageRequired)
    .matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
});

export const intellioSchema = Yup.object({
  project: Yup.object({
    clientName: Yup.string()
      .required(errorMessageRequired)
      .matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
    projectType: Yup.string().required(errorMessageRequired),
    description: Yup.string()
      .optional()
      .matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
    teamsLink: Yup.string().nullable().matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
    teamsId: Yup.string().uuid('Must be a valid UUID.').required(errorMessageRequired),
    opportunityId: Yup.string()
      .required(errorMessageRequired)
      .matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
    channelId: Yup.string().optional().matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
    sharepointFolderURL: Yup.string()
      .nullable()
      .when('indexToUse', {
        is: (val: string) => [IndexType.OPENAI, IndexType.GRAPH_RAG].includes(val as IndexType),
        then: Yup.string()
          .required(errorMessageRequired)
          .matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
        otherwise: Yup.string()
          .nullable()
          .matches(regexNoStartEndWhiteSpace, errorMessageNoWhiteSpace),
      }),
    ignoredFolders: excludedFolderSchema,
    ignoredFiles: excludedFileSchema,
  }),
});
